import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UsersIcon, DollarSignIcon, BoxIcon, CalendarIcon, CheckCircleIcon, InfoIcon, ArrowLeftIcon, ArrowRightIcon, GavelIcon, PackageIcon, RefreshCwIcon, TargetIcon, PlusIcon, PercentIcon, HelpCircleIcon, AlertCircleIcon, ChevronRightIcon, ShieldIcon, BarChart2Icon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import SyndicateWorkflow from '../components/SyndicateWorkflow';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
const SyndicateCreation: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [syndicateType, setSyndicateType] = useState('');
  const [syndicateName, setSyndicateName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [minimumContribution, setMinimumContribution] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [targetAuctionId, setTargetAuctionId] = useState('');
  const [description, setDescription] = useState('');
  const [deviceAllocation, setDeviceAllocation] = useState([{
    type: 'iPhone 13 Pro',
    percentage: 40,
    value: 40000
  }, {
    type: 'iPhone 13',
    percentage: 30,
    value: 30000
  }, {
    type: 'iPhone 12 Pro',
    percentage: 20,
    value: 20000
  }, {
    type: 'iPhone 12',
    percentage: 10,
    value: 10000
  }]);
  const activeAuctions = [{
    id: 'AUC-2023-1245',
    title: 'Bulk iPhone 12 & 13 Models',
    devices: 25,
    currentBid: '$10,250',
    timeLeft: '12h 32m',
    seller: 'TechTrade Solutions'
  }, {
    id: 'AUC-2023-1244',
    title: 'Samsung Galaxy S22 Collection',
    devices: 18,
    currentBid: '$6,120',
    timeLeft: '1d 8h',
    seller: 'Mobile Source LLC'
  }, {
    id: 'AUC-2023-1243',
    title: 'Google Pixel 6 Pro Lot',
    devices: 12,
    currentBid: '$3,950',
    timeLeft: '2d 4h',
    seller: 'Wireless Wholesale Co'
  }];
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };
  const handleSubmit = () => {
    // In a real app, this would submit the form data to an API
    navigate('/syndicate-detail/syn-2023-new');
  };
  const handleDeviceAllocationChange = (index, field, value) => {
    const newAllocation = [...deviceAllocation];
    newAllocation[index][field] = value;
    if (field === 'percentage') {
      // Recalculate value based on percentage
      newAllocation[index].value = (parseInt(targetAmount) || 100000) * (value / 100);
    } else if (field === 'value') {
      // Recalculate percentage based on value
      newAllocation[index].percentage = (value / (parseInt(targetAmount) || 100000) * 100).toFixed(1);
    }
    setDeviceAllocation(newAllocation);
  };
  const addDeviceAllocation = () => {
    setDeviceAllocation([...deviceAllocation, {
      type: '',
      percentage: 0,
      value: 0
    }]);
  };
  const removeDeviceAllocation = index => {
    const newAllocation = [...deviceAllocation];
    newAllocation.splice(index, 1);
    setDeviceAllocation(newAllocation);
  };
  return <div className="space-y-6" data-id="element-2172">
      <div className="flex items-center justify-between" data-id="element-2173">
        <div className="flex items-center" data-id="element-2174">
          <Button variant="ghost" size="sm" className="mr-2" onClick={() => navigate('/syndicate-hub')} data-id="element-2175">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-2176" />
            Back to Hub
          </Button>
          <h1 className="text-2xl font-bold" data-id="element-2177">Create New Syndicate</h1>
        </div>
      </div>
      <SyndicateWorkflow currentStep={currentStep} data-id="element-2178" />
      {currentStep === 1 && <Card data-id="element-2179">
          <CardHeader data-id="element-2180">
            <CardTitle data-id="element-2181">Step 1: Syndicate Type & Basic Information</CardTitle>
            <CardDescription data-id="element-2182">
              Define the type of syndicate you want to create and provide basic
              details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-2183">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-2184">
              <Card className={`cursor-pointer transition-all ${syndicateType === 'auction' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setSyndicateType('auction')} data-id="element-2185">
                <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-2186">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4" data-id="element-2187">
                    <GavelIcon className="h-6 w-6" data-id="element-2188" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2189">Auction Syndicate</h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-2190">
                    Pool resources to bid on a specific auction as a group
                  </p>
                  {syndicateType === 'auction' && <Badge variant="success" className="mt-auto" data-id="element-2191">
                      Selected
                    </Badge>}
                </CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all ${syndicateType === 'bulk' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setSyndicateType('bulk')} data-id="element-2192">
                <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-2193">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4" data-id="element-2194">
                    <PackageIcon className="h-6 w-6" data-id="element-2195" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2196">Bulk Purchase Syndicate</h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-2197">
                    Combine funds to make large bulk purchases from suppliers
                  </p>
                  {syndicateType === 'bulk' && <Badge variant="success" className="mt-auto" data-id="element-2198">
                      Selected
                    </Badge>}
                </CardContent>
              </Card>
              <Card className={`cursor-pointer transition-all ${syndicateType === 'liquidation' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setSyndicateType('liquidation')} data-id="element-2199">
                <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-2200">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4" data-id="element-2201">
                    <RefreshCwIcon className="h-6 w-6" data-id="element-2202" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2203">Liquidation Syndicate</h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-2204">
                    Access carrier and corporate liquidation opportunities
                    together
                  </p>
                  {syndicateType === 'liquidation' && <Badge variant="success" className="mt-auto" data-id="element-2205">
                      Selected
                    </Badge>}
                </CardContent>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2206">
              <div className="space-y-2" data-id="element-2207">
                <label htmlFor="syndicate-name" className="text-sm font-medium" data-id="element-2208">
                  Syndicate Name
                </label>
                <input id="syndicate-name" type="text" className="w-full p-2 rounded-md border bg-background" placeholder="e.g., Premium iPhone Collective" value={syndicateName} onChange={e => setSyndicateName(e.target.value)} data-id="element-2209" />
              </div>
              <div className="space-y-2" data-id="element-2210">
                <label htmlFor="end-date" className="text-sm font-medium" data-id="element-2211">
                  End Date
                </label>
                <input id="end-date" type="date" className="w-full p-2 rounded-md border bg-background" value={endDate} onChange={e => setEndDate(e.target.value)} data-id="element-2212" />
                <p className="text-xs text-muted-foreground" data-id="element-2213">
                  This is the deadline for contributions and when the syndicate
                  will make its purchase.
                </p>
              </div>
              <div className="space-y-2" data-id="element-2214">
                <label htmlFor="target-amount" className="text-sm font-medium" data-id="element-2215">
                  Target Capital
                </label>
                <div className="relative" data-id="element-2216">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2217">
                    $
                  </span>
                  <input id="target-amount" type="number" className="w-full pl-8 p-2 rounded-md border bg-background" placeholder="100000" value={targetAmount} onChange={e => setTargetAmount(e.target.value)} data-id="element-2218" />
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-2219">
                  The total amount of capital the syndicate aims to raise.
                </p>
              </div>
              <div className="space-y-2" data-id="element-2220">
                <label htmlFor="min-contribution" className="text-sm font-medium" data-id="element-2221">
                  Minimum Contribution
                </label>
                <div className="relative" data-id="element-2222">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2223">
                    $
                  </span>
                  <input id="min-contribution" type="number" className="w-full pl-8 p-2 rounded-md border bg-background" placeholder="5000" value={minimumContribution} onChange={e => setMinimumContribution(e.target.value)} data-id="element-2224" />
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-2225">
                  The minimum amount each participant must contribute.
                </p>
              </div>
              <div className="space-y-2 md:col-span-2" data-id="element-2226">
                <label htmlFor="syndicate-description" className="text-sm font-medium" data-id="element-2227">
                  Description
                </label>
                <textarea id="syndicate-description" className="w-full p-2 rounded-md border bg-background min-h-[100px]" placeholder="Describe the purpose and goals of this syndicate..." value={description} onChange={e => setDescription(e.target.value)} data-id="element-2228" />
              </div>
              <div className="space-y-2 md:col-span-2" data-id="element-2229">
                <div className="flex items-center justify-between" data-id="element-2230">
                  <label className="text-sm font-medium" data-id="element-2231">
                    Syndicate Visibility
                  </label>
                  <div className="flex items-center space-x-4" data-id="element-2232">
                    <div className={`flex items-center space-x-2 cursor-pointer ${isPublic ? 'text-primary' : 'text-muted-foreground'}`} onClick={() => setIsPublic(true)} data-id="element-2233">
                      <div className={`h-4 w-4 rounded-full border ${isPublic ? 'bg-primary border-primary' : 'border-muted-foreground'}`} data-id="element-2234"></div>
                      <span data-id="element-2235">Public</span>
                    </div>
                    <div className={`flex items-center space-x-2 cursor-pointer ${!isPublic ? 'text-primary' : 'text-muted-foreground'}`} onClick={() => setIsPublic(false)} data-id="element-2236">
                      <div className={`h-4 w-4 rounded-full border ${!isPublic ? 'bg-primary border-primary' : 'border-muted-foreground'}`} data-id="element-2237"></div>
                      <span data-id="element-2238">Invite Only</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-2239">
                  Public syndicates are visible to all platform users.
                  Invite-only syndicates are only visible to those you invite.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-6 flex justify-end" data-id="element-2240">
            <Button onClick={handleNextStep} data-id="element-2241">
              Continue <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-2242" />
            </Button>
          </CardFooter>
        </Card>}
      {currentStep === 2 && <Card data-id="element-2243">
          <CardHeader data-id="element-2244">
            <CardTitle data-id="element-2245">Step 2: Target Opportunity</CardTitle>
            <CardDescription data-id="element-2246">
              {syndicateType === 'auction' ? 'Select the auction you want to target with this syndicate' : syndicateType === 'bulk' ? 'Define the bulk purchase details' : 'Specify the liquidation opportunity details'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-2247">
            {syndicateType === 'auction' && <>
                <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2248">
                  <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2249" />
                  <div data-id="element-2250">
                    <p className="text-sm font-medium" data-id="element-2251">
                      Auction Syndicate Information
                    </p>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2252">
                      Select an active auction that you want to target with this
                      syndicate. The syndicate will pool funds to place
                      competitive bids on the selected auction.
                    </p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2253">
                  <h3 className="text-sm font-medium" data-id="element-2254">Select Target Auction</h3>
                  {activeAuctions.map(auction => <div key={auction.id} className={`p-4 border rounded-md cursor-pointer transition-all ${targetAuctionId === auction.id ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setTargetAuctionId(auction.id)} data-id="element-2255">
                      <div className="flex items-start justify-between" data-id="element-2256">
                        <div data-id="element-2257">
                          <div className="flex items-center" data-id="element-2258">
                            <h3 className="font-medium" data-id="element-2259">{auction.title}</h3>
                            <Badge variant="success" className="ml-2" data-id="element-2260">
                              Active
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1" data-id="element-2261">
                            ID: {auction.id} • Seller: {auction.seller}
                          </p>
                        </div>
                        <div className="flex items-center text-sm" data-id="element-2262">
                          <ClockIcon className="h-4 w-4 mr-1 text-yellow-500" data-id="element-2263" />
                          <span data-id="element-2264">{auction.timeLeft} left</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-2265">
                        <div data-id="element-2266">
                          <p className="text-xs text-muted-foreground" data-id="element-2267">
                            Devices
                          </p>
                          <p className="font-medium" data-id="element-2268">{auction.devices}</p>
                        </div>
                        <div data-id="element-2269">
                          <p className="text-xs text-muted-foreground" data-id="element-2270">
                            Current Bid
                          </p>
                          <p className="font-medium text-green-600" data-id="element-2271">
                            {auction.currentBid}
                          </p>
                        </div>
                        <div className="flex items-center justify-end" data-id="element-2272">
                          {targetAuctionId === auction.id && <Badge variant="success" data-id="element-2273">Selected</Badge>}
                        </div>
                      </div>
                    </div>)}
                  <Button variant="outline" className="w-full" data-id="element-2274">
                    <GavelIcon className="mr-2 h-4 w-4" data-id="element-2275" />
                    Browse More Auctions
                  </Button>
                </div>
              </>}
            {(syndicateType === 'bulk' || syndicateType === 'liquidation') && <>
                <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2276">
                  <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2277" />
                  <div data-id="element-2278">
                    <p className="text-sm font-medium" data-id="element-2279">
                      {syndicateType === 'bulk' ? 'Bulk Purchase Information' : 'Liquidation Opportunity Information'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1" data-id="element-2280">
                      {syndicateType === 'bulk' ? 'Define the details of the bulk purchase you want to make. Specify the types of devices and their allocation in the total purchase.' : 'Specify the details of the liquidation opportunity. Define the expected inventory composition and allocation.'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2281">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2282">
                    <div className="space-y-2" data-id="element-2283">
                      <label htmlFor="supplier" className="text-sm font-medium" data-id="element-2284">
                        {syndicateType === 'bulk' ? 'Supplier' : 'Liquidation Source'}
                      </label>
                      <input id="supplier" type="text" className="w-full p-2 rounded-md border bg-background" placeholder={syndicateType === 'bulk' ? 'e.g., TechTrade Solutions' : 'e.g., Verizon Wireless'} data-id="element-2285" />
                    </div>
                    <div className="space-y-2" data-id="element-2286">
                      <label htmlFor="expected-devices" className="text-sm font-medium" data-id="element-2287">
                        Expected Number of Devices
                      </label>
                      <input id="expected-devices" type="number" className="w-full p-2 rounded-md border bg-background" placeholder="e.g., 50" data-id="element-2288" />
                    </div>
                    <div className="space-y-2" data-id="element-2289">
                      <label htmlFor="estimated-price-per-device" className="text-sm font-medium" data-id="element-2290">
                        Estimated Avg. Price Per Device
                      </label>
                      <div className="relative" data-id="element-2291">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2292">
                          $
                        </span>
                        <input id="estimated-price-per-device" type="number" className="w-full pl-8 p-2 rounded-md border bg-background" placeholder="e.g., 500" data-id="element-2293" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-2294">
                      <label htmlFor="expected-roi" className="text-sm font-medium" data-id="element-2295">
                        Expected ROI
                      </label>
                      <div className="relative" data-id="element-2296">
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2297">
                          %
                        </span>
                        <input id="expected-roi" type="number" className="w-full pr-8 p-2 rounded-md border bg-background" placeholder="e.g., 15" data-id="element-2298" />
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </CardContent>
          <CardFooter className="border-t pt-6 flex justify-between" data-id="element-2299">
            <Button variant="outline" onClick={handlePreviousStep} data-id="element-2300">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2301" />
              Back
            </Button>
            <Button onClick={handleNextStep} data-id="element-2302">
              Continue <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-2303" />
            </Button>
          </CardFooter>
        </Card>}
      {currentStep === 3 && <Card data-id="element-2304">
          <CardHeader data-id="element-2305">
            <CardTitle data-id="element-2306">Step 3: Inventory Allocation</CardTitle>
            <CardDescription data-id="element-2307">
              Define how the acquired inventory will be allocated
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-2308">
            <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2309">
              <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2310" />
              <div data-id="element-2311">
                <p className="text-sm font-medium" data-id="element-2312">Allocation Information</p>
                <p className="text-sm text-muted-foreground mt-1" data-id="element-2313">
                  Specify how the syndicate's capital will be allocated across
                  different device types. Each member will receive devices
                  proportional to their contribution.
                </p>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2314">
              <div className="flex items-center justify-between" data-id="element-2315">
                <h3 className="text-sm font-medium" data-id="element-2316">Device Allocation Plan</h3>
                <TooltipProvider data-id="element-2317">
                  <Tooltip data-id="element-2318">
                    <TooltipTrigger asChild data-id="element-2319">
                      <Button variant="outline" size="sm" onClick={addDeviceAllocation} data-id="element-2320">
                        <PlusIcon className="h-4 w-4 mr-1" data-id="element-2321" />
                        Add Device Type
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent data-id="element-2322">
                      <p data-id="element-2323">Add another device type to the allocation plan</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-3" data-id="element-2324">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground px-2" data-id="element-2325">
                  <div className="col-span-5" data-id="element-2326">Device Type</div>
                  <div className="col-span-3" data-id="element-2327">Allocation %</div>
                  <div className="col-span-3" data-id="element-2328">Value ($)</div>
                  <div className="col-span-1" data-id="element-2329"></div>
                </div>
                {deviceAllocation.map((device, index) => <div key={index} className="grid grid-cols-12 gap-4 items-center" data-id="element-2330">
                    <div className="col-span-5" data-id="element-2331">
                      <input type="text" className="w-full p-2 rounded-md border bg-background" placeholder="e.g., iPhone 13 Pro" value={device.type} onChange={e => handleDeviceAllocationChange(index, 'type', e.target.value)} data-id="element-2332" />
                    </div>
                    <div className="col-span-3" data-id="element-2333">
                      <div className="relative" data-id="element-2334">
                        <input type="number" className="w-full p-2 pr-8 rounded-md border bg-background" placeholder="40" value={device.percentage} onChange={e => handleDeviceAllocationChange(index, 'percentage', parseInt(e.target.value) || 0)} data-id="element-2335" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2336">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="col-span-3" data-id="element-2337">
                      <div className="relative" data-id="element-2338">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2339">
                          $
                        </span>
                        <input type="number" className="w-full p-2 pl-8 rounded-md border bg-background" placeholder="40000" value={device.value} onChange={e => handleDeviceAllocationChange(index, 'value', parseInt(e.target.value) || 0)} data-id="element-2340" />
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end" data-id="element-2341">
                      {deviceAllocation.length > 1 && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeDeviceAllocation(index)} data-id="element-2342">
                          <AlertCircleIcon className="h-4 w-4 text-red-500" data-id="element-2343" />
                        </Button>}
                    </div>
                  </div>)}
              </div>
              <div className="flex items-center justify-between pt-2 border-t" data-id="element-2344">
                <span className="font-medium" data-id="element-2345">Total</span>
                <div className="flex items-center space-x-8" data-id="element-2346">
                  <span className="font-medium" data-id="element-2347">
                    {deviceAllocation.reduce((acc, curr) => acc + curr.percentage, 0)}
                    %
                  </span>
                  <span className="font-medium" data-id="element-2348">
                    $
                    {deviceAllocation.reduce((acc, curr) => acc + curr.value, 0).toLocaleString()}
                  </span>
                </div>
              </div>
              {deviceAllocation.reduce((acc, curr) => acc + curr.percentage, 0) !== 100 && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-md text-sm flex items-center" data-id="element-2349">
                  <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" data-id="element-2350" />
                  <span data-id="element-2351">
                    Total allocation percentage must equal 100%. Current total:{' '}
                    {deviceAllocation.reduce((acc, curr) => acc + curr.percentage, 0)}
                    %
                  </span>
                </div>}
            </div>
            <div className="pt-6 border-t" data-id="element-2352">
              <h3 className="text-sm font-medium mb-4" data-id="element-2353">Allocation Preview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2354">
                <div data-id="element-2355">
                  <h4 className="text-sm text-muted-foreground mb-2" data-id="element-2356">
                    Distribution by Device Type
                  </h4>
                  <div className="space-y-2" data-id="element-2357">
                    {deviceAllocation.map((device, index) => <div key={index} className="flex items-center" data-id="element-2358">
                        <div className="w-24 text-sm truncate" data-id="element-2359">
                          {device.type}
                        </div>
                        <div className="flex-1 mx-2" data-id="element-2360">
                          <div className="h-2 rounded-full bg-muted overflow-hidden" data-id="element-2361">
                            <div className={`h-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-yellow-500' : 'bg-purple-500'}`} style={{
                        width: `${device.percentage}%`
                      }} data-id="element-2362"></div>
                          </div>
                        </div>
                        <div className="text-sm text-right w-24" data-id="element-2363">
                          {device.percentage}% (${device.value.toLocaleString()}
                          )
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className="p-4 border rounded-md bg-muted/30" data-id="element-2364">
                  <h4 className="text-sm font-medium mb-3" data-id="element-2365">
                    Member Allocation Example
                  </h4>
                  <div className="space-y-3" data-id="element-2366">
                    <div className="flex justify-between text-sm" data-id="element-2367">
                      <span className="text-muted-foreground" data-id="element-2368">
                        Your contribution:
                      </span>
                      <span className="font-medium" data-id="element-2369">
                        $25,000 (25% of total)
                      </span>
                    </div>
                    <div className="border-t pt-3" data-id="element-2370">
                      <p className="text-sm text-muted-foreground mb-2" data-id="element-2371">
                        You would receive:
                      </p>
                      <ul className="space-y-1 text-sm" data-id="element-2372">
                        {deviceAllocation.map((device, index) => <li key={index} className="flex justify-between" data-id="element-2373">
                            <span data-id="element-2374">{device.type}:</span>
                            <span className="font-medium" data-id="element-2375">
                              {Math.round(device.value * 0.25 / (parseInt(targetAmount) || 100000) * 100) / 100}{' '}
                              units
                            </span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-6 flex justify-between" data-id="element-2376">
            <Button variant="outline" onClick={handlePreviousStep} data-id="element-2377">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2378" />
              Back
            </Button>
            <Button onClick={handleNextStep} data-id="element-2379">
              Continue <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-2380" />
            </Button>
          </CardFooter>
        </Card>}
      {currentStep === 4 && <Card data-id="element-2381">
          <CardHeader data-id="element-2382">
            <CardTitle data-id="element-2383">Step 4: Review & Launch</CardTitle>
            <CardDescription data-id="element-2384">
              Review the syndicate details and launch it
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-2385">
            <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2386">
              <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2387" />
              <div data-id="element-2388">
                <p className="text-sm font-medium" data-id="element-2389">Syndicate Review</p>
                <p className="text-sm text-muted-foreground mt-1" data-id="element-2390">
                  Review all the details of your syndicate before launching it.
                  Once launched, some parameters cannot be changed.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2391">
              <Card data-id="element-2392">
                <CardHeader data-id="element-2393">
                  <CardTitle className="text-lg" data-id="element-2394">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2395">
                  <div className="flex justify-between" data-id="element-2396">
                    <span className="text-sm text-muted-foreground" data-id="element-2397">Name</span>
                    <span className="font-medium" data-id="element-2398">
                      {syndicateName || 'Premium iPhone Collective'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2399">
                    <span className="text-sm text-muted-foreground" data-id="element-2400">Type</span>
                    <span className="font-medium capitalize" data-id="element-2401">
                      {syndicateType || 'Auction'} Syndicate
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2402">
                    <span className="text-sm text-muted-foreground" data-id="element-2403">
                      Target Capital
                    </span>
                    <span className="font-medium" data-id="element-2404">
                      ${parseInt(targetAmount).toLocaleString() || '100,000'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2405">
                    <span className="text-sm text-muted-foreground" data-id="element-2406">
                      Min. Contribution
                    </span>
                    <span className="font-medium" data-id="element-2407">
                      $
                      {parseInt(minimumContribution).toLocaleString() || '5,000'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2408">
                    <span className="text-sm text-muted-foreground" data-id="element-2409">
                      End Date
                    </span>
                    <span className="font-medium" data-id="element-2410">
                      {endDate || '2023-08-15'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2411">
                    <span className="text-sm text-muted-foreground" data-id="element-2412">
                      Visibility
                    </span>
                    <span className="font-medium" data-id="element-2413">
                      {isPublic ? 'Public' : 'Invite Only'}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <Card data-id="element-2414">
                <CardHeader data-id="element-2415">
                  <CardTitle className="text-lg" data-id="element-2416">Target Opportunity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-2417">
                  {syndicateType === 'auction' && <>
                      <div className="flex justify-between" data-id="element-2418">
                        <span className="text-sm text-muted-foreground" data-id="element-2419">
                          Target Auction
                        </span>
                        <span className="font-medium" data-id="element-2420">
                          {targetAuctionId || 'AUC-2023-1245'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-2421">
                        <span className="text-sm text-muted-foreground" data-id="element-2422">
                          Auction Title
                        </span>
                        <span className="font-medium" data-id="element-2423">
                          {activeAuctions.find(a => a.id === targetAuctionId)?.title || 'Bulk iPhone 12 & 13 Models'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-2424">
                        <span className="text-sm text-muted-foreground" data-id="element-2425">
                          Current Bid
                        </span>
                        <span className="font-medium" data-id="element-2426">
                          {activeAuctions.find(a => a.id === targetAuctionId)?.currentBid || '$10,250'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-2427">
                        <span className="text-sm text-muted-foreground" data-id="element-2428">
                          Time Left
                        </span>
                        <span className="font-medium" data-id="element-2429">
                          {activeAuctions.find(a => a.id === targetAuctionId)?.timeLeft || '12h 32m'}
                        </span>
                      </div>
                    </>}
                  {(syndicateType === 'bulk' || syndicateType === 'liquidation') && <>
                      <div className="flex justify-between" data-id="element-2430">
                        <span className="text-sm text-muted-foreground" data-id="element-2431">
                          {syndicateType === 'bulk' ? 'Supplier' : 'Liquidation Source'}
                        </span>
                        <span className="font-medium" data-id="element-2432">TechTrade Solutions</span>
                      </div>
                      <div className="flex justify-between" data-id="element-2433">
                        <span className="text-sm text-muted-foreground" data-id="element-2434">
                          Expected Devices
                        </span>
                        <span className="font-medium" data-id="element-2435">50 units</span>
                      </div>
                      <div className="flex justify-between" data-id="element-2436">
                        <span className="text-sm text-muted-foreground" data-id="element-2437">
                          Avg. Price Per Device
                        </span>
                        <span className="font-medium" data-id="element-2438">$500</span>
                      </div>
                      <div className="flex justify-between" data-id="element-2439">
                        <span className="text-sm text-muted-foreground" data-id="element-2440">
                          Expected ROI
                        </span>
                        <span className="font-medium" data-id="element-2441">15%</span>
                      </div>
                    </>}
                </CardContent>
              </Card>
            </div>
            <Card data-id="element-2442">
              <CardHeader data-id="element-2443">
                <CardTitle className="text-lg" data-id="element-2444">Allocation Plan</CardTitle>
              </CardHeader>
              <CardContent data-id="element-2445">
                <div className="overflow-x-auto" data-id="element-2446">
                  <table className="w-full" data-id="element-2447">
                    <thead data-id="element-2448">
                      <tr className="border-b" data-id="element-2449">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-2450">
                          Device Type
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground" data-id="element-2451">
                          Allocation %
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground" data-id="element-2452">
                          Value ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2453">
                      {deviceAllocation.map((device, index) => <tr key={index} className="border-b" data-id="element-2454">
                          <td className="py-3 px-4 font-medium" data-id="element-2455">
                            {device.type}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-2456">
                            {device.percentage}%
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-2457">
                            ${device.value.toLocaleString()}
                          </td>
                        </tr>)}
                      <tr className="bg-muted/30" data-id="element-2458">
                        <td className="py-3 px-4 font-medium" data-id="element-2459">Total</td>
                        <td className="py-3 px-4 text-right font-medium" data-id="element-2460">
                          {deviceAllocation.reduce((acc, curr) => acc + curr.percentage, 0)}
                          %
                        </td>
                        <td className="py-3 px-4 text-right font-medium" data-id="element-2461">
                          $
                          {deviceAllocation.reduce((acc, curr) => acc + curr.value, 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <Card data-id="element-2462">
              <CardHeader data-id="element-2463">
                <CardTitle className="text-lg" data-id="element-2464">Fees & Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-2465">
                <div className="flex justify-between" data-id="element-2466">
                  <span className="text-sm text-muted-foreground" data-id="element-2467">
                    Platform Fee
                  </span>
                  <span className="font-medium" data-id="element-2468">
                    2.5% of total capital raised
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2469">
                  <span className="text-sm text-muted-foreground" data-id="element-2470">
                    Escrow Fee
                  </span>
                  <span className="font-medium" data-id="element-2471">
                    1.0% of total capital raised
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2472">
                  <span className="text-sm text-muted-foreground" data-id="element-2473">
                    Creator Fee
                  </span>
                  <span className="font-medium" data-id="element-2474">0% (optional)</span>
                </div>
                <div className="flex justify-between" data-id="element-2475">
                  <span className="text-sm text-muted-foreground" data-id="element-2476">
                    Syndicate Terms
                  </span>
                  <Button variant="link" className="h-auto p-0" data-id="element-2477">
                    View Terms & Conditions
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="flex items-start p-4 border rounded-md" data-id="element-2478">
              <ShieldIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2479" />
              <div data-id="element-2480">
                <p className="text-sm font-medium" data-id="element-2481">Secure Escrow Protection</p>
                <p className="text-sm text-muted-foreground mt-1" data-id="element-2482">
                  All funds contributed to this syndicate will be held in a
                  secure escrow account until the target opportunity is
                  acquired. If the syndicate fails to acquire the target, all
                  funds will be returned to contributors minus any applicable
                  fees.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2" data-id="element-2483">
              <div className="h-5 w-5 rounded border flex items-center justify-center" data-id="element-2484">
                <CheckCircleIcon className="h-4 w-4 text-primary" data-id="element-2485" />
              </div>
              <span className="text-sm" data-id="element-2486">
                I understand and agree to the syndicate terms and conditions
              </span>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-6 flex justify-between" data-id="element-2487">
            <Button variant="outline" onClick={handlePreviousStep} data-id="element-2488">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2489" />
              Back
            </Button>
            <Button onClick={handleSubmit} data-id="element-2490">
              Launch Syndicate <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-2491" />
            </Button>
          </CardFooter>
        </Card>}
    </div>;
};
export default SyndicateCreation;